import React, { useEffect, useState } from 'react';

import moment from 'jalali-moment';

import { AcceptedConversationsChart, Tab } from '../../../components';
import ReportsHeader from '../ReportsHeader';
import {
  ClockTwoToneIcon,
  LabelIcon,
  MessageTwoToneIcon,
  RadioIcon,
} from '../../../assets/icons';
import './styles.scss';
import AppDatePicker from '../../DatePicker';
import { useDispatch } from 'react-redux';
import {
  fetchOperatorReportsStart,
  setOperatorReports,
} from '../../../redux/operators/operators.actions';
import { useParams } from 'react-router-dom';
import { LineWave } from 'react-loader-spinner';
import { useLocation } from 'react-router';
import {
  convertDateToEnglish,
  convertTimeZoneToPersian,
  getCalenderLastDays,
} from '../../../utils/date';
import useTranslation from 'hooks/useTranslation';

const OperatorDetailReport = ({ operatorReports }) => {
  const lastWeekDate = getCalenderLastDays(6, 'days');
  const [selectedDate, setSelectedDate] = useState(lastWeekDate);
  const { getTranslation, getAppLocale } = useTranslation('STATISTICS');
  const { t } = getTranslation();
  const locale = getAppLocale();

  const dispatch = useDispatch();
  const params = useParams();

  useEffect(() => {
    if (params.operatorId) {
      dispatch(
        fetchOperatorReportsStart({
          operator_id: params.operatorId,
          startDate: null,
          endDate: null,
        })
      );
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    return () => {
      dispatch(setOperatorReports(null));
    };
  }, [location]);

  const getSelectedStatistic = () => {
    let startDate = convertDateToEnglish(selectedDate[0], '-');
    let endDate = convertDateToEnglish(selectedDate[1], '-');
    dispatch(
      fetchOperatorReportsStart({
        operator_id: params.operatorId,
        startDate,
        endDate,
      })
    );
  };

  const renderTitleSection = (
    <div className='flex flex-ai-c header_wrapper'>
      <h1 className='h1'>{t('OPERATOR_STATS_MAIN_TITLE')}</h1>
      <div className='flex actions_wrapper'>
        <AppDatePicker
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClose={getSelectedStatistic}
        />
        {/* <button href='#/' className='overlay-button download' disabled>
          <img src={DownloadIcon} alt='' />
          {t('OPERATOR_STATS_DOWNLOAD_BTN_TEXT')}
        </button> */}
      </div>
    </div>
  );

  const renderProfileBox = (imageUrl, name, email, position) => (
    <div className='box'>
      <img className='image' src={imageUrl} alt='' />
      <div className='info-wrapper'>
        <h3>{name}</h3>
        <p>{email}</p>
        <div className='position'>
          <img className='label-icon' src={LabelIcon} alt='' />
          {position}
        </div>
      </div>
    </div>
  );

  const renderConversationsStatistics = (
    acceptedConversations,
    closedConversations
  ) => (
    <div className='statistics-wrapper'>
      <h4>
        <img src={MessageTwoToneIcon} alt='' />
        {t('OPERATOR_STATS_CONVS_STATS')}
      </h4>
      <div className='item bordered'>
        <img src={RadioIcon} alt='' />
        <div className='field'>
          <span className='name'>{t('OPERATOR_STATS_ACCEPTED_CONVS')}</span>
          <span className='value'>{`${acceptedConversations} ${t(
            'OPERATOR_STATS_CONV_PREFIX'
          )}`}</span>
        </div>
      </div>
      <div className='item'>
        <img src={RadioIcon} alt='' />
        <div className='field'>
          <span className='name'>{t('OPERATOR_STATS_ENDED_CONVS')}</span>
          <span className='value'>{`${closedConversations} ${t(
            'OPERATOR_STATS_CONV_PREFIX'
          )}`}</span>
        </div>
      </div>
    </div>
  );

  const renderWorkTimeStatistics = (startOnline, endOnline) => (
    <div className='statistics-wrapper time'>
      <h4>
        <img src={ClockTwoToneIcon} alt='' />
        {t('OPERATOR_STATS_ACTIVE_TIME')}
      </h4>
      <div className='item bordered'>
        <img src={RadioIcon} alt='' />
        <div className='field'>
          <span className='name'>{t('OPERATOR_STATS_FIRST_ONLINE')}</span>
          <span className='value'>{`${startOnline}`}</span>
        </div>
      </div>
      <div className='item'>
        <img src={RadioIcon} alt='' />
        <div className='field'>
          <span className='name'>{t('OPERATOR_STATS_LAST_ONLINE')}</span>
          <span className='value'>{`${endOnline}`}</span>
        </div>
      </div>
    </div>
  );

  const renderConversationsStatisticsTab = (
    acceptedConversations,
    closedConversations,
    closedAgentConversations,
    deniedConversations,
    conversationsChartData
  ) => (
    <div label={t('OPERATOR_STATS_CHART_TAB_CONVS_STATS')}>
      <div className='conversation-chart'>
        <div className='field-wrapper'>
          <div className='field'>
            <div className='name accepted'>
              {t('OPERATOR_STATS_CHART_LEGEND_ACCEPTED_CONVS')}
            </div>
            <div className='value'>{acceptedConversations}</div>
          </div>
          <div className='field'>
            <div className='name closed'>
              {' '}
              {t('OPERATOR_STATS_CHART_LEGEND_ENDED_CONVS')}{' '}
            </div>
            <div className='value'>{closedConversations}</div>
          </div>
          <div className='field'>
            <div className='name closedAgent'>
              {' '}
              {t('OPERATOR_STATS_CHART_LEGEND_UNANSWERED_CONVS')}{' '}
            </div>
            <div className='value'>{closedAgentConversations}</div>
          </div>
          <div className='field'>
            <div className='name denied'>
              {t('OPERATOR_STATS_CHART_LEGEND_DENIED_CONVS')}{' '}
            </div>
            <div className='value'>{deniedConversations}</div>
          </div>
        </div>
        <AcceptedConversationsChart
          acceptedConversations={conversationsChartData.acceptedConversations}
          closedConversations={conversationsChartData.closedConversations}
          closedAgentConversations={
            conversationsChartData.closedAgentConversations
          }
          deniedConversations={conversationsChartData.deniedConversations}
          isLastWeek={false}
        />
      </div>
    </div>
  );

  const renderWorkTimeStatisticsTab = (data, totalActivityTime) => (
    <div label={t('OPERATOR_STATS_CHART_TAB_ACTIVE_TIME')}>
      <div className='conversation-chart worktime-tab'>
        <div className='field-wrapper'>
          <div className='field'>
            <div className='name'>
              {t('OPERATOR_STATS_TOTAL_HOUR_LABEL')} :{' '}
            </div>
            <div className='value'>{`${totalActivityTime} ${t(
              'OPERATOR_STATS_TOTAL_HOUR_PREFIX'
            )} `}</div>
          </div>
        </div>
        <AcceptedConversationsChart
          acceptedConversations={data}
          isLastWeek={false}
        />
      </div>
    </div>
  );

  const getDateBasedOnLocale = (date) => {
    if (!date) return '-';

    if (date.includes('T') && locale === 'fa') {
      return convertTimeZoneToPersian(date);
    }

    return moment(date).format('YYYY MM DD, hh:mm');
  };

  return (
    <>
      <ReportsHeader
        title={t('OPERATOR_STATS_HEADER_TITLE')}
        description={t('OPERATOR_STATS_HEADER_DESC')}
      />
      {operatorReports && Object.keys(operatorReports).length > 0 ? (
        <main className='operator-report'>
          {renderTitleSection}
          <div className='profile-wrapper'>
            {renderProfileBox(
              operatorReports.imageUrl,
              operatorReports.name,
              operatorReports.email,
              operatorReports.position
            )}
            <div className='flex flex-wrap'>
              {renderConversationsStatistics(
                operatorReports.acceptedConversations,
                operatorReports.closedConversations
              )}
              {renderWorkTimeStatistics(
                getDateBasedOnLocale(operatorReports.startOnline),
                getDateBasedOnLocale(operatorReports.endOnline)
              )}
            </div>
          </div>
          <Tab wrapperClass='tabs-wrapper'>
            {renderConversationsStatisticsTab(
              operatorReports.acceptedConversations,
              operatorReports.closedConversations,
              operatorReports.closedAgentConversations,
              operatorReports.deniedConversations,
              operatorReports.conversationsChartData
            )}
            {renderWorkTimeStatisticsTab(
              operatorReports.activitiesChartData,
              operatorReports.totalActivityTime
            )}
          </Tab>
        </main>
      ) : (
        <LineWave
          height={50}
          width={50}
          color='rgb(177, 14, 132)'
          ariaLabel='line-wave'
          wrapperStyle={{}}
          wrapperClass=''
          visible={true}
        />
      )}
    </>
  );
};

export default React.memo(OperatorDetailReport);
